import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../components/LanguageContext';

// Terms of Service & Privacy Policy, shown INSIDE the app instead of sending the
// user to a browser tab on the backend's technical URL.
//
// The document ships with the app (public/legal/terms-and-privacy.html, copied
// from Backend/legal by scripts/copy-legal.js at build time), so it opens
// instantly and offline in the store app. It is not framed — hosting sends
// X-Frame-Options: DENY — but fetched and rendered into a Shadow DOM, so its own
// stylesheet cannot leak into the app and the app's cannot restyle it.
// Its scripts are dropped; in-page links (the table of contents) scroll inside
// the panel instead of changing the app's URL.

export type LegalSection = 'terms' | 'privacy';
const EVENT = 'asset-desk:open-legal';
const DOC_URL = `${process.env.PUBLIC_URL || ''}/legal/terms-and-privacy.html`;

/** Open the in-app Terms (or Privacy) panel from anywhere. */
export function openLegal(section: LegalSection) {
  window.dispatchEvent(new CustomEvent(EVENT, { detail: section }));
}

let docCache: Promise<{ style: string; body: string }> | null = null;
function loadDoc() {
  if (!docCache) {
    docCache = fetch(DOC_URL)
      .then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        parsed.querySelectorAll('script').forEach((s) => s.remove());
        // Its rules for html/body would match nothing inside the shadow root:
        // point them at the wrapper so the document keeps its own layout.
        const style = Array.from(parsed.querySelectorAll('style')).map((s) => s.textContent || '').join('\n')
          .replace(/(^|[\s,}])(html|body)(?=[\s,{.:#[>])/g, '$1.legal-doc');
        return { style, body: parsed.body ? parsed.body.innerHTML : '' };
      })
      .catch((e) => { docCache = null; throw e; });
  }
  return docCache;
}

export const LegalModalHost: React.FC = () => {
  const { language } = useLanguage();
  const de = language === 'de';
  const [section, setSection] = useState<LegalSection | null>(null);
  const [failed, setFailed] = useState(false);
  const hostRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onOpen = (e: Event) => { setFailed(false); setSection(((e as CustomEvent).detail as LegalSection) || 'terms'); };
    window.addEventListener(EVENT, onOpen);
    return () => window.removeEventListener(EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (!section) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setSection(null); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [section]);

  useEffect(() => {
    if (!section || !hostRef.current) return;
    let alive = true;
    const host = hostRef.current;
    loadDoc().then(({ style, body }) => {
      if (!alive) return;
      const root = host.shadowRoot || host.attachShadow({ mode: 'open' });
      // :host replaces the document's own <body> margins inside the panel.
      root.innerHTML = `<style>${style}\n:host{display:block}</style><div class="legal-doc">${body}</div>`;
      root.querySelectorAll('a[href^="#"]').forEach((a) => {
        a.addEventListener('click', (ev) => {
          ev.preventDefault();
          const id = (a.getAttribute('href') || '').slice(1);
          const target = root.getElementById(id);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      });
      root.querySelectorAll('a[href^="http"]').forEach((a) => { a.setAttribute('target', '_blank'); a.setAttribute('rel', 'noopener noreferrer'); });
      const jump = root.getElementById(section === 'privacy' ? 'privacy-policy' : 'terms-of-service');
      if (section === 'privacy' && jump) jump.scrollIntoView({ block: 'start' });
      else if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }).catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [section]);

  if (!section) return null;
  const title = section === 'privacy'
    ? (de ? 'Datenschutzerklärung' : 'Privacy Policy')
    : (de ? 'Nutzungsbedingungen' : 'Terms of Service');

  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      style={{ position: 'fixed', inset: 0, zIndex: 100000, background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        padding: 'max(12px, env(safe-area-inset-top)) 16px 12px', borderBottom: '1px solid rgba(0,0,0,0.10)', background: '#000',
      }}>
        <span style={{ color: '#fff', fontWeight: 700, fontSize: 16, fontFamily: 'Montserrat, sans-serif' }}>{title}</span>
        <button onClick={() => setSection(null)} aria-label={de ? 'Schließen' : 'Close'}
          style={{ background: '#D6B160', border: 'none', borderRadius: 999, width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
          <X size={18} color="#000" />
        </button>
      </div>
      {de && (
        <div style={{ padding: '8px 16px', background: '#faf6ea', color: '#6F5518', fontSize: 13 }}>
          Das Dokument ist derzeit nur auf Englisch verfügbar.
        </div>
      )}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {failed ? (
          <p style={{ padding: 24, color: '#444' }}>
            {de ? 'Das Dokument konnte nicht geladen werden. ' : 'The document could not be loaded. '}
            <a href="https://portal.investmenter.com/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#8A6C22' }}>
              {de ? 'Im Browser öffnen' : 'Open in browser'}
            </a>
          </p>
        ) : (
          <div ref={hostRef} />
        )}
      </div>
    </div>
  );
};
